import Dump from 'components/Dump';
// CORE
import { useAppSelector } from 'hooks/redux';
import React, { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { rootUiActions } from 'app-components/flux/root-ui';
// APP-COMPONENTS
import { useFetchOnce } from 'app-components/hooks/utils';
import { loadState, useLoadState } from 'app-components/hooks/utils';
import { getCommonConfig } from 'app-components/utils/core';

import OpenInNewIcon from '@mui/icons-material/OpenInNewRounded';
// MUI
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

// DATA
import * as fundingAccountsActions from 'data/fundingAccounts/actions';
import * as fundingAccountSelectors from 'data/fundingAccounts/selectors';

// COMPONENTS
import type { AccountLockoutDialogProps } from 'components/Dialogs/A2A/actions';
import { showOnboardAccountsDialog } from 'components/Dialogs/A2A/actions';
import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import LoadingView from 'components/LoadingView';
import QButton from 'components/QButton';
import ZeroStateView from 'components/ZeroStateView';

const useStyles = makeStyles()((theme) => ({
  successOrFailureTab: {
    paddingBottom: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  icon: {
    height: 16,
    width: 16,
  },
}));

const AccountLockoutDialog = React.memo((props: AccountLockoutDialogProps) => {
  const isMounted = useRef<boolean>();
  const { dialogId, fundingAccountId, onClose, errorCode } = props;
  const dispatch = useDispatch();
  const { classes } = useStyles();
  useFetchOnce(fundingAccountsActions.getFundingAccounts, fundingAccountSelectors);

  // ================ SELECTORS ==================
  const fundingAccount = useAppSelector((state) =>
    fundingAccountSelectors.getFundingAccountById(state, fundingAccountId ?? ''),
  );

  //  ================= VIEW STATE LOGIC =====================
  const view = useLoadState(fundingAccountSelectors);
  const mainView = useMemo(() => {
    const showSkeletonView = loadState.skeletonStates.includes(view);
    const showTryAgainView = loadState.tryAgainStates.includes(view) || loadState.updateRequiredStates.includes(view);
    const showNoDataView = loadState.noDataStates.includes(view);

    const showReadyView = loadState.readyStates.includes(view);

    let myMainView = '';
    if (showReadyView) {
      myMainView = 'READY';
    } else if (showNoDataView) {
      myMainView = 'READY';
    } else if (showTryAgainView) {
      myMainView = 'FAILED';
    } else if (showSkeletonView) {
      myMainView = 'LOADING';
    }

    return myMainView;
  }, [view]);

  // ========== EFFECTS =============

  // track mounted state because of async promise
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // =================== STATES ==================
  const [hideDialog, setHideDialog] = useState(false);

  // ========== CALLBACKS =============
  const handleClose = () => {
    dispatch(rootUiActions.removeDialog(dialogId));
    onClose?.();
  };

  const getFundingAccounts = useCallback(() => {
    dispatch(fundingAccountsActions.getFundingAccounts(undefined));
  }, [dispatch]);

  const onboardNewAccount = (_e?: any) => {
    setHideDialog(true);
    dispatch(
      showOnboardAccountsDialog({
        onClose: handleClose,
        overrideOpening: true,
        initiator: 'lockout screen',
      }),
    );
  };

  let failureReason: 'generic' | 'temporary-issue' | 'name' | 'other' | 'limit' = 'generic';

  if (errorCode) {
    switch (errorCode) {
      case 'IFS-AOV-0009':
      case 'IFS-AOV-0007':
      case 'IFS-AOV-0011':
      case 'IFS-AOV-0010':
      case 'IFS-AOV-3000':
        failureReason = 'generic';
        break;
      case 'IFS-AOV-0008':
        failureReason = 'temporary-issue';
        break;
      case 'IFS-AOV-1000':
        failureReason = 'name';
        break;
      case 'IFS-1021-2011':
        failureReason = 'limit';
        break;
      default:
        failureReason = 'other';
        break;
    }
  }

  const errorText = (
    <Typography component='span' variant='captionRegular' color='text.secondary'>
      Want more info?
      <Link
        href={getCommonConfig()?.external?.contact_support_tutorial ?? ''}
        target='_blank'
        underline='hover'
        variant='captionRegular'
        color='primary'
      >
        &nbsp; Contact Support
        <OpenInNewIcon className={classes.icon} />
      </Link>
      {errorCode && (
        <>
          &nbsp;and use reference code&nbsp;
          <Typography component='span' variant='captionMedium' color='text.secondary'>
            {errorCode}
          </Typography>
        </>
      )}
    </Typography>
  );

  // =============== DERIVED VALUES ===================
  let dialogTitle = 'Account not eligible';
  if (failureReason === 'temporary-issue' || failureReason === 'other' || failureReason === 'limit')
    dialogTitle = 'Set up account transfers';

  // ================= RENDER ================
  return (
    <StdDialog
      dialogId='DIALOG_TYPE_FUNDING_ACCOUNT_LOCKOUT'
      onClose={handleClose}
      open={!hideDialog}
      sharedcomponentid='DIALOG_TYPE_FUNDING_ACCOUNT_LOCKOUT'
      showCloseButton
      size='md'
      title={dialogTitle}
    >
      <StdDialogContent id='DIALOG_TYPE_FUNDING_ACCOUNT_LOCKOUT'>
        <Dump obj={fundingAccount ?? {}} />
        {mainView === 'READY' && failureReason === 'generic' && (
          <Box className={classes.successOrFailureTab}>
            <ZeroStateView
              id='generic-failure'
              title={'Account not eligible for transfers'}
              description={
                'We’re sorry—this account is not eligible for money transfers in Simplifi. We know that’s frustrating, but you can always try setting up a different account.'
              }
              qIllustration='starSad'
            />
            {errorText}
          </Box>
        )}
        {mainView === 'READY' && failureReason === 'other' && (
          <Box className={classes.successOrFailureTab}>
            <ZeroStateView
              id='other-failure'
              title={'Uh-oh!'}
              description={'We hit a snag. Please try again.'}
              qIllustration='planeCrash'
            />
          </Box>
        )}
        {mainView === 'READY' && failureReason === 'limit' && (
          <Box className={classes.successOrFailureTab}>
            <ZeroStateView
              id='other-failure'
              title={'Account Limit Reached'}
              description={
                "Unfortunately, you've reached the maximum number of accounts that can be enabled for transfers at this time. We're working on expanding this limit in the future."
              }
              qIllustration='FInoneConnected'
            />
          </Box>
        )}
        {mainView === 'READY' && failureReason === 'name' && (
          <Box className={classes.successOrFailureTab}>
            <ZeroStateView
              id='name-failure'
              title={'Account not eligible for transfers'}
              description={
                'This account isn’t eligible because the owner’s name doesn’t match the name on your Simplifi account. We know that’s frustrating, but you can try using a different account.'
              }
              qIllustration='starSad'
            />
            {errorText}
          </Box>
        )}
        {mainView === 'READY' && failureReason === 'temporary-issue' && (
          <Box className={classes.successOrFailureTab}>
            <ZeroStateView
              id='temporary-failure'
              title='We hit a snag..'
              description={
                'Your institution is reporting a temporary issue with your account that’s preventing us from enabling transfers. Please contact them to resolve it, then try again.'
              }
              qIllustration='FInoneConnected'
            />
            {errorText}
          </Box>
        )}
        {/* mainView === 'READY' && failureReason === 'micro deposits' && (
          <Box className={classes.successOrFailureTab}>
            <ZeroStateView
              id='microdeposit-failure'
              title={'Account could not be verified'}
              description={
                'We’re sorry, but you’ve exceeded the maximum number of verification attempts. To verify this account for transfers, please go to the Help Center, and initiate a chat with one of our support agents.'
              }
              qIllustration='starSad'
            />
            {errorText}
          </Box>
        ) */}
        {mainView === 'LOADING' && (
          <Box
            paddingTop={20}
            paddingBottom={20}
            display='flex'
            alignItems='center'
            flexDirection='column'
            justifyContent='center'
          >
            <LoadingView size={100} />
          </Box>
        )}
        {mainView === 'FAILED' && (
          <ZeroStateView
            className={classes.successOrFailureTab}
            button={
              <QButton id={'try-again-button'} variant='outlined' onClick={getFundingAccounts}>
                TRY AGAIN
              </QButton>
            }
            id='failed-to-fetch'
            title='Unable to fetch data'
            description="We can't fetch your data from the server. Please try again"
            qIllustration='planeCrash'
          />
        )}
        {mainView === 'EMPTY' && (
          <ZeroStateView
            className={classes.successOrFailureTab}
            id='zero-state'
            title='No data available'
            description='Your account may be in error. Please try again'
            qIllustration='emptyBox'
          />
        )}
      </StdDialogContent>
      {mainView === 'READY' && failureReason !== 'limit' && (
        <StdDialogActions
          primary={{
            children: failureReason === 'other' ? 'Try again' : 'Set up another account',
            onClick: onboardNewAccount,
            variant: 'contained',
          }}
          secondary={{
            children: 'close',
            onClick: handleClose,
            variant: 'outlined',
          }}
        />
      )}
      {mainView === 'READY' && failureReason === 'limit' && (
        <StdDialogActions
          primary={{
            children: 'close',
            onClick: handleClose,
            variant: 'outlined',
          }}
        />
      )}
    </StdDialog>
  );
});

AccountLockoutDialog.displayName = 'AccountLockoutDialog';

export default AccountLockoutDialog;
